import { FIREBASE_AUTH } from "@/config/firebase";
import { ENV } from "@/config/env";
import { ApiRequestError, AuthError, TransientError } from "@/types/auth";
import { API_ERROR_BUS } from "@/utils/eventBus";

interface FetchApiOptions extends RequestInit {
  readonly skipAuth?: boolean;
}

// ATE-CR-117 — task 11 FIX: dev-only Playwright hook. When the e2e harness
// pre-stamps `window.__ATE_E2E_TOKEN__` (a function returning a Promise<string>
// that resolves to a real Firebase emulator id_token), client.ts will use that
// token instead of trying to read `FIREBASE_AUTH.currentUser.getIdToken()`.
// Production builds drop this branch (Vite dead-code-eliminates the
// `import.meta.env.DEV` guard). This mirrors the existing AuthContext
// `__ATE_E2E_AUTH__` override but, unlike that one, plumbs through the actual
// HTTP-bound token so end-to-end tests can hit a real backend.
async function getToken(forceRefresh: boolean): Promise<string | null> {
  if (
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    typeof (window as unknown as { __ATE_E2E_TOKEN__?: unknown })
      .__ATE_E2E_TOKEN__ === "function"
  ) {
    const tokenGetter = (
      window as unknown as { __ATE_E2E_TOKEN__: () => Promise<string> }
    ).__ATE_E2E_TOKEN__;
    return tokenGetter();
  }
  const user = FIREBASE_AUTH.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

async function parseErrorDetail(response: Response): Promise<string | undefined> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      const body = (await response.json()) as { detail?: string };
      return body.detail;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

async function performFetch(
  path: string,
  init: FetchApiOptions,
  token: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  const url = new URL(path, ENV.API_URL).toString();
  return fetch(url, { ...init, headers });
}

export async function fetchApi<T = unknown>(
  path: string,
  init: FetchApiOptions = {},
): Promise<T> {
  const { data } = await fetchApiWithResponse<T>(path, init);
  return data;
}

// ATE-CR-159 — like fetchApi, but also returns the underlying Response so
// callers can read response headers (e.g. `Retry-After`). Same auth retry
// + error mapping semantics as fetchApi.
export async function fetchApiWithResponse<T = unknown>(
  path: string,
  init: FetchApiOptions = {},
): Promise<{ data: T; response: Response }> {
  let token = init.skipAuth ? null : await getToken(false);
  let response = await performFetch(path, init, token);

  if (response.status === 401 && !init.skipAuth) {
    token = await getToken(true);
    response = await performFetch(path, init, token);
  }

  if (response.ok) {
    if (response.status === 204) return { data: undefined as T, response };
    const data = (await response.json()) as T;
    return { data, response };
  }

  const serverMessage = await parseErrorDetail(response);
  const msg = serverMessage ?? `Request failed: ${response.status}`;

  let error: ApiRequestError;
  if (response.status === 401 || response.status === 403) {
    error = new AuthError(msg, response.status, serverMessage);
  } else if (response.status >= 500) {
    error = new TransientError(msg, response.status, serverMessage);
  } else {
    error = new ApiRequestError(msg, "PERMANENT", response.status, serverMessage);
  }

  API_ERROR_BUS.emit({ message: msg, status: response.status });
  throw error;
}
